import {
  DumbbellIcon,
  EvChargingStationIcon,
  HasBabyCarraige,
  HasParking,
  HasPool,
  HasSpa,
  HasWifi,
  KidsFriendlyIcon,
  ParkingIcon,
  PetFriendlyIcon,
  SpaWellnessIcon,
} from "@/app/components/common/Icons";
import { publicEnvConfig } from "../config/config";
import { useTranslations } from "next-intl";
/**
 * This constant is used to set the value of payment type.
 */
export const PAYMENT_TYPES = {
  CREDIT_CARD: "creditCard",
  CRYPTO: "crypto",
} as const;

export const AmenityIcons: { [key: string]: string } = {
  "Non-Smoking Facility": "no-smoking.svg",
  "Cable/Satellite TV": "television.svg",
  "Hair Dryer": "hair-dryer.svg",
  "Flat Screen TV": "television.svg",
  "Coffee Maker": "coffee-maker.svg",
  "Alarm Clock": "alarm.svg",
  "Internet Access - Free in room": "wifi-icon.svg",
  "Internet Access - in room for fee": "wifi-icon.svg",
  "Wheelchair Access": "wheelchair.svg",
  "In room safe": "safe-in-room.svg",
  "Mini-Bar": "mini-bar-icon.svg",
  Refrigerator: "refrigerator.svg",
  Balcony: "balcony.svg",
  Microwave: "microwave.svg",
} as const;

export const TRAVELER_TYPES = ["adults", "children", "infants"];

export const getTravelerOptions = (t: ReturnType<typeof useTranslations>) => [
  { label: t("HomePageFlights.adults"), value: "2", description: "17+" },
  { label: t("HomePageFlights.children"), value: "0", description: "2 - 17" },
  { label: t("HomePageFlights.infants"), value: "0", description: "<2" },
];

export const SEARCH_PARAMS = {
  CITY: "city",
  COUNTRY: "country",
  DESTINATION: "destination",
  CHECKIN: "checkIn",
  CHECKOUT: "checkOut",
  ADULTS: "adults",
  CHILDREN: "children",
  INFANTS: "infants",
  CURRENCY: "currency",
  ID: "id",
};

export const MIN_SEARCH_LENGTH = 2;
export const MAX_TRAVELER_COUNT = 10;

export const DATE_FORMAT = "DD-MM-YY";
export const API_DATE_FORMAT = "YYYY-MM-DD";

export const MAX_SEARCH_TIMEOUT_IN_MS = 45000;
export const MAX_REMINDER_TIMEOUT_IN_MS = 1800000;
export const MAX_REMINDER_INTERVAL_IN_MS = 1200000;

export const WALLET_HINTS = [
  {
    label: "Sign In",
    description:
      "Securely sign in to unlock a world of exclusive benefits, including special hotel offers tailored for Web3 users.",
    icon: "/assets/v2/icons/signin.svg",
  },
  {
    label: "Get Personal Offers",
    description:
      "Explore a curated selection of exclusive hotel offers, accessible only to wallet-connected users. Enjoy unbeatable rates and personalized deals.",
    icon: "/assets/v2/icons/personal-offer.svg",
  },
  {
    label: "Book & Check-out",
    description:
      "Seamlessly book your preferred accommodation and complete your check out process. Choose to pay conveniently with over 200+ cryptocurrency options, making your travel experience as smooth as possible.",
    icon: "/assets/v2/icons/book-and-checkout.svg",
  },
];

const getDestinationId = (id1: string, id2: string): string => {
  return publicEnvConfig?.env === "dev" ? id1 : id2;
};

export const TOP_DESTINATIONS = [
  {
    image: "/assets/v2/images/barcelona.jpg",
    city: "Barcelona",
    countryName: "Spain",
    countryCode: "ES",
    description:
      "Barcelona captivates with its breathtaking architecture, vibrant street life, beautiful beaches, mouthwatering cuisine, and a lively arts and culture scene.",
    destination: "Barcelona & surrounding area",
    tag: "Very Popular",
    id: getDestinationId("C27853", "C27790"),
  },
  {
    image: "/assets/v2/images/rome.jpg",
    city: "Rome",
    countryName: "Italy",
    countryCode: "IT",
    destination: "Latium",
    description:
      "Rome offers ancient ruins, stunning architecture, delicious cuisine, vibrant piazzas, and a unique blend of history and modernity.",
    tag: "New Hotels",
    id: getDestinationId("C65657", "C65599"),
  },
  {
    image: "/assets/v2/images/paris.jpg",
    city: "Paris",
    countryName: "France",
    countryCode: "FR",
    destination: "Île de France",
    description:
      "Paris is a must-visit for its iconic landmarks, rich history, art, culture, culinary delights, and romantic atmosphere.",
    tag: "Very Popular",
    id: getDestinationId("C51589", "C51528"),
  },
  {
    image: "/assets/v2/images/amsterdam.jpg",
    city: "Amsterdam",
    countryName: "Netherlands",
    countryCode: "NL",
    destination: "Amsterdam & surrounding area",
    description:
      "Amsterdam charms with its picturesque canals, historic architecture, world-class museums, vibrant cycling culture, and a relaxed atmosphere perfect for exploration.",
    id: getDestinationId("C74894", "C74839"),
  },
  {
    image: "/assets/v2/images/berlin.jpg",
    city: "Berlin",
    countryName: "Germany",
    countryCode: "DE",
    destination: "Berlin",
    description:
      "Berlin beckons with its fascinating history, vibrant nightlife, diverse art scene, multicultural atmosphere, and a thriving alternative and subculture.",
    id: getDestinationId("C21066", "C21003"),
  },
  {
    image: "/assets/v2/images/london.jpg",
    city: "London",
    countryName: "United Kingdom",
    countryCode: "GB",
    destination: "London & surrounding area",
    description:
      "London entices with its iconic landmarks, world-class museums, vibrant theater scene, diverse culinary offerings, and a blend of old-world charm and modern dynamism.",
    tag: "New Hotels",
    id: getDestinationId("C35216", "C35155"),
  },
  {
    image: "/assets/v2/images/prague.jpg",
    city: "Prague",
    countryName: "Czech Republic",
    countryCode: "CZ",
    destination: "Prague & surrounding area",
    description:
      "Prague enchants with its fairy-tale-like architecture, rich history, charming cobblestone streets, hearty cuisine, and a bohemian ambiance that is truly captivating.",
    id: getDestinationId("C19427", "C19343"),
  },
  {
    image: "/assets/v2/images/vienna.jpg",
    city: "Vienna",
    countryName: "Austria",
    countryCode: "AT",
    destination: "Vienna & surrounding area",
    description:
      "Vienna allures with its elegant palaces, classical music heritage, captivating art and culture scene, delectable coffeehouse culture, and architectural grandeur at every turn.",
    tag: "New Hotels",
    id: getDestinationId("C4934", "C4834"),
  },
  {
    image: "/assets/v2/images/lisbon.jpg",
    city: "Lisbon",
    countryName: "Portugal",
    countryCode: "PT",
    destination: "Lisbon Region and Setúbal",
    description:
      "Lisbon beckons with its colorful tiled facades, charming neighborhoods, delicious seafood, Fado music, and breathtaking views from its hilly streets and viewpoints.",
    id: getDestinationId("C82331", "C82276"),
  },
  {
    image: "/assets/v2/images/budapest.jpg",
    city: "Budapest",
    countryName: "Hungary",
    countryCode: "HU",
    destination: "Budapest & surrounding area",
    description:
      "Budapest captivates with its stunning architecture, thermal baths, vibrant nightlife, rich history, delicious cuisine, and the picturesque Danube River flowing through the city.",
    tag: "New Hotels",
    id: getDestinationId("C54336", "C54274"),
  },
  {
    image: "/assets/v2/images/istanbul.jpg",
    city: "Istanbul",
    countryName: "Turkey",
    countryCode: "TR",
    destination: "Istanbul (province)",
    description:
      "Istanbul mesmerizes with its magnificent mosques, bustling bazaars, rich history, delightful cuisine, vibrant street life, and the enchanting blend of East and West.",
    id: getDestinationId("C89170", "C89117"),
  },
  {
    image: "/assets/v2/images/athens.jpg",
    city: "Athens",
    countryName: "Greece",
    countryCode: "GR",
    description:
      "Athens allures with its ancient wonders like the Acropolis, fascinating mythology, Mediterranean charm, delicious cuisine, and vibrant modern energy.",
    destination: "Attica (Athens and surrounding area)",
    tag: "Very Popular",
    id: getDestinationId("C52150", "C52089"),
  },
  {
    image: "/assets/v2/images/stockholm.jpg",
    city: "Stockholm",
    countryName: "Sweden",
    destination: "Stockholm & surrounding area",
    countryCode: "SE",
    description:
      "Stockholm delights with its scenic waterways, picturesque islands, historic architecture, Nordic design, rich cultural heritage, and vibrant culinary scene.",
    id: getDestinationId("C86948", "C86895"),
  },
  {
    image: "/assets/v2/images/florence.jpg",
    city: "Florence",
    countryName: "Italy",
    countryCode: "IT",
    destination: "Tuscany",
    description:
      "Florence enchants with its Renaissance masterpieces, iconic Duomo, romantic atmosphere, world-class art galleries, delectable cuisine, and charming streets lined with history.",
    tag: "Very Popular",
    id: getDestinationId("C69125", "C69068"),
  },
  {
    image: "/assets/v2/images/dublin.jpg",
    city: "Dublin",
    countryName: "Ireland",
    countryCode: "IE",
    destination: "Dublin & surrounding area",
    description:
      "Dublin charms with its lively pub culture, warm hospitality, rich literary heritage, historic landmarks, traditional music, and vibrant street life.",
    tag: "New Hotels",
    id: getDestinationId("C3", "C54283"), // city not found
  },
  {
    image: "/assets/v2/images/krakow.jpg",
    city: "Krakow",
    countryName: "Poland",
    countryCode: "PL",
    destination: "Lesser Poland Voivodeship",
    description:
      "Krakow enthralls with its well-preserved medieval archi-tecture, poignant history, vibrant cultural scene, charming old town, and proximity to Auschwitz-Birkenau Memorial and Museum.",
    id: getDestinationId("C79904", "C79849"),
  },
  {
    image: "/assets/v2/images/copenhagen.jpg",
    city: "Copenhagen",
    countryName: "Denmark",
    countryCode: "DK",
    destination: "Copenhagen",
    description:
      "Copenhagen captivates with its modern design, charming canals, world-renowned cuisine, cycling culture, beautiful parks, and a cozy hygge atmosphere.",
    tag: "New Hotels",
    id: getDestinationId("C17292", "C17234"),
  },
  {
    image: "/assets/v2/images/edinburgh.jpg",
    city: "Edinburgh",
    countryName: "United Kingdom",
    countryCode: "GB",
    destination: "Scotland",
    description:
      "Edinburgh enchants with its medieval Old Town, majestic castle, lively festivals, haunting history, scenic landscapes, and thriving arts and literary scene.",
    id: getDestinationId("C37156", "C37089"),
  },
  {
    image: "/assets/v2/images/new-york.jpg",
    city: "New York",
    countryName: "United States of America",
    destination: "New York",
    countryCode: "US",
    description:
      "New York dazzles with its iconic landmarks, vibrant cityscape, diverse neighborhoods, world-class cuisine, unmatched shopping, and unparalleled cultural offerings.",
    tag: "Very Popular",
    id: getDestinationId("C3", "C96604"), // city not found on the list
  },
  {
    image: "/assets/v2/images/dubai.jpg",
    city: "Dubai",
    countryName: "United Arab Emirates",
    destination: "Dubai",
    countryCode: "AE",
    description:
      "Dubai amazes with its futuristic architecture, luxurious shopping, stunning skyline, world-class entertainment, desert adventures, and a melting pot of cultures.",
    tag: "Very Popular",
    id: getDestinationId("C63", "C66"),
  },
];

export const HOTEL_SUMMARY = [
  "Amazing hotel",
  "Very clean and tidy",
  "Wonderful rooms",
  "Great value for money",
  "Excellent service",
];

export const OPTIONS: DateTimeFormatOptions = {
  weekday: "short",
  month: "short",
  day: "numeric",
};

export const REVIEWS = [
  {
    img: "/assets/v2/images/Frame 964-7.jpg",
    review: "Effortless booking with Web3 - secure and easy!",
  },
  {
    img: "/assets/v2/images/Frame 964-8.jpg",
    review: "Web3 tech rocks for hotel bookings!",
  },
  {
    img: "/assets/v2/images/Frame 964-9.jpg",
    review: "Quick and easy hotel booking with Web3. Love it",
  },
  {
    img: "/assets/v2/images/Frame 964-10.jpg",
    review: "No hidden fees, just honest prices. Great!",
  },
  {
    img: "/assets/v2/images/Frame 964-11.jpg",
    review: "Last-minute trip? Try Sleap. Quick and crypto-friendly.",
  },
  {
    img: "/assets/v2/images/Frame 964-12.jpg",
    review: "Bye, middlemen! Hello, Web3 hotels!",
  },
];

export const LOGO_LISTS = [
  { src: "/assets/v2/images/made-by-apes.png", alt: "Made by Apes" },
  { src: "/assets/v2/images/Calque 2.png", alt: "World of Women" },
  { src: "/assets/v2/images/animoca.png", alt: "Animoca" },
  { src: "/assets/v2/images/Camino.png", alt: "Camino" },
  {
    src: "/assets/v2/images/Forbesweb3logo_146x127.png",
    alt: "Forbesweb3",
    width: 112,
    height: 31,
  },
  { src: "/assets/v2/images/Frame 1154.png", alt: "rug radio" },
  {
    src: "/assets/v2/images/poweredbyape.png",
    alt: "ape coin",
    width: 146,
    height: 42,
  },
];

export const FEATUIRED_LISTS = [
  "/assets/v2/images/Group.png",
  "/assets/v2/images/techstartups.png",
  "/assets/v2/images/Yahoo-Logo.png",
  "/assets/v2/images/schema-publisher-logo-benzinga.png",
  "/assets/v2/images/278927736_305625345061589_7627463802972348540_n.png",
  "/assets/v2/images/Frame-11 1.png",
  "/assets/v2/images/2560px-Bloomberg_logo.png",
];

export const FEATUIRED_LISTS_LIGHT = [
  "/assets/v2/images/Frame1.png",
  "/assets/v2/images/Frame2.png",
  "/assets/v2/images/Frame3.png",
  "/assets/v2/images/Frame4.png",
  "/assets/v2/images/Frame5.png",
  "/assets/v2/images/Frame6.png",
  "/assets/v2/images/Frame7.png",
];

export const PAGES_LINKS = {
  HOMEPAGE: "/",
  ACCOUNT: "/account",
  CONTACT: "/contact",
  ABOUT: "/about",
  DISCORD: "https://discord.com/invite/sleap",
  NFT: "/nft",
  POINTS: "/points",
};
export const CARD_ICONS = [
  { calling: "hasSpa", icon: HasSpa },
  { calling: "hasParking", icon: HasParking },
  { calling: "hasFamilyRooms", icon: HasBabyCarraige },
  { calling: "hasWifi", icon: HasWifi },
  { calling: "hasPool", icon: HasPool },
  {
    calling: "hasEVChargingStation",
    icon: EvChargingStationIcon,
  },
  { calling: "isPetFriendly", icon: PetFriendlyIcon },
  { calling: "hasGym", icon: DumbbellIcon },
];

export enum HotelRoomOfferBoardType {
  none = "none",
  breakfast = "Breakfast included",
  allInclusive = "All Inclusive",
  americanBreakfast = "American breakfast",
  asianBreakfast = "Asian breakfast",
  breakfastIncluded = "Breakfast included",
  breakfastBuffet = "Breakfast Buffet",
  breakfastFor1 = "Breakfast for 1 guest",
  breakfastFor2 = "Breakfast for 2 guests",
  chineseBreakfast = "Chinese breakfast",
  continentalBreakfast = "Continental Breakfast",
  englishBreakfast = "English breakfast",
  dinner = "Dinner",
  fullBoard = "Breakfast, lunch and dinner included",
  halfBoard = "Breakfast and dinner or lunch included",
  halfBoardDinner = "Breakfast and dinner",
  halfBoardLunch = "Breakfast and lunch",
  irishBreakfast = "Irish breakfast",
  israeliBreakfast = "Israeli breakfast",
  japaneseBreakfast = "Japanese breakfast",
  lunch = "Lunch",
  scandinavianBreakfast = "Scandinavian breakfast",
  scottishBreakfast = "Scottish breakfast",
  softAllInclusive = "Soft all inclusive",
  someMeal = "Meals included",
  superAllInclusive = "Super all inclusive",
  ultraAllInclusive = "Ultra all inclusive",
  freeBreakfast = "Free breakfast",
}

export const BOARD_MAPPING = [
  {
    code: "1",
    key: "All_inclusive",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.allInclusive,
  },
  {
    code: "2",
    key: "American",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.americanBreakfast,
  },
  {
    code: "3",
    key: "Bed_And_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.allInclusive,
  },
  {
    code: "4",
    key: "Buffet_And_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    code: "5",
    key: "Caribbean_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    code: "6",
    key: "Continental_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    code: "7",
    key: "English_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.englishBreakfast,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "8",
    key: "European_plan",
    text: "",
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "9",
    key: "Family_plan",
    text: "",
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "10",
    key: "Full_board",
    text: HotelRoomOfferBoardType.fullBoard,
  },
  {
    code: "11",
    key: "Full_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "12",
    key: "Half_board",
    text: HotelRoomOfferBoardType.halfBoard,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "13",
    key: "As_brochured",
    text: "",
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "14",
    key: "Room_only",
    text: HotelRoomOfferBoardType.none,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "15",
    key: "Self_catering",
    text: "",
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "16",
    key: "Bermuda",
    text: "",
  },
  {
    code: "17",
    key: "Dinner_And_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.halfBoardDinner,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "18",
    key: "Family_American",
    text: "",
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "19",
    key: "Breakfast",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "20",
    key: "Modified",
    text: "",
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "255",
    key: "Full_board_And_drink",
    text: HotelRoomOfferBoardType.fullBoard,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "256",
    key: "Half_board_And_drink",
    text: HotelRoomOfferBoardType.halfBoard,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "257",
    key: "All_Inclusive_Premium",
    text: HotelRoomOfferBoardType.superAllInclusive,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "258",
    key: "Gala_Dinner",
    text: HotelRoomOfferBoardType.dinner,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "259",
    key: "All_Inclusive_Light",
    text: HotelRoomOfferBoardType.softAllInclusive,
  },
  {
    code: "260",
    key: "Bed_And_breakfast_And_Half_board",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.allInclusive,
  },
  {
    code: "free_breakfast",
    key: "free_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfast,
  },
  {
    code: "room_only",
    key: "room_only",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.none,
  },
  {
    code: "all-inclusive",
    key: "all-inclusive",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.allInclusive,
  },
  {
    code: "american-breakfast",
    key: "american-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.americanBreakfast,
  },
  {
    code: "asian-breakfast",
    key: "asian-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.asianBreakfast,
  },
  {
    code: "breakfast",
    key: "breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfastIncluded,
  },
  {
    code: "breakfast-buffet",
    key: "breakfast-buffet",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfastBuffet,
  },
  {
    code: "breakfast-for-1",
    key: "breakfast-for-1",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfastFor1,
  },
  {
    code: "breakfast-for-2",
    key: "breakfast-for-2",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.breakfastFor2,
  },
  {
    code: "chinese-breakfast",
    key: "chinese-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.chineseBreakfast,
  },
  {
    code: "continental-breakfast",
    key: "continental-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.continentalBreakfast,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "dinner",
    key: "dinner",
    text: HotelRoomOfferBoardType.dinner,
  },
  {
    code: "english-breakfast",
    key: "english-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.englishBreakfast,
  },
  {
    code: "full-board",
    key: "full-board",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.fullBoard,
  },
  {
    code: "half-board",
    key: "half-board",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.halfBoard,
  },
  {
    code: "half-board-dinner",
    key: "half-board-dinner",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.halfBoardDinner,
  },
  {
    code: "half-board-lunch",
    key: "half-board-lunch",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.halfBoardLunch,
  },
  {
    code: "irish-breakfast",
    key: "irish-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.irishBreakfast,
  },
  {
    code: "israeli-breakfast",
    key: "israeli-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.israeliBreakfast,
  },
  {
    code: "japanese-breakfast",
    key: "japanese-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.japaneseBreakfast,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "lunch",
    key: "lunch",
    text: HotelRoomOfferBoardType.lunch,
  },
  {
    icon: "/assets/v2/icons/mug-outline.svg",
    code: "nomeal",
    key: "nomeal",
    text: HotelRoomOfferBoardType.none,
  },
  {
    code: "scandinavian-breakfast",
    key: "scandinavian-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.scandinavianBreakfast,
  },
  {
    code: "scottish-breakfast",
    key: "scottish-breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.scottishBreakfast,
  },
  {
    code: "soft-all-inclusive",
    key: "soft-all-inclusive",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.softAllInclusive,
  },
  {
    code: "some-meal",
    key: "some-meal",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.someMeal,
  },
  {
    code: "super-all-inclusive",
    key: "super-all-inclusive",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.superAllInclusive,
  },
  {
    code: "ultra-all-inclusive",
    key: "ultra-all-inclusive",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.ultraAllInclusive,
  },
  {
    code: "free_breakfast",
    key: "free_breakfast",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.freeBreakfast,
  },
  {
    code: "free_breakfast#free_lunch#free_dinner",
    key: "free_breakfast#free_lunch#free_dinner",
    icon: "/assets/v2/icons/mug-outline.svg",
    text: HotelRoomOfferBoardType.fullBoard,
  },
];

export const NETWORK_RPC_PROVIDES: { [key: string]: string } = {
  ETH: `https://mainnet.infura.io/v3/${publicEnvConfig.infuraProjectId}`,
  BASE: "https://mainnet.base.org",
};

export const STRIPE_PROCESSING_FEE = 0.0299;

export const FALLBACK_FROM_EMAIL_SENDER = "support@sleap.io";
export const FALLBACK_EMAIL_SUBJECT_PREFIX = "Gate Travel";
export const FALLBACK_SUPPORT_EMAIL = "support@staynex.vip";
